import React, { useCallback, useEffect, useRef, useState } from 'react';
import { i18n } from '../../i18n/i18n';
import { editorStateKey } from '../../utils/editorChanges';

/**
 * The frame of the parent editors (task, reward, tool, room — 2026-10-17, Jens): a header that stays
 * at the top with an ✕, content that only scrolls up and down (it used to wobble sideways on
 * phones), and the ✕, Esc or a tap outside all ask to close — see useCloseGuard.
 */

// Esc closes only the window on top (the camera or the "save?" question before the editor).
const escapeStack: Array<{ current: () => void }> = [];
function onEscapeKey(e: KeyboardEvent) {
  if (e.key !== 'Escape' || !escapeStack.length) return;
  e.preventDefault();
  escapeStack[escapeStack.length - 1].current();
}

export function useEscape(handler: () => void, active = true) {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!active) return;
    const entry = { current: () => ref.current() };
    escapeStack.push(entry);
    if (escapeStack.length === 1) window.addEventListener('keydown', onEscapeKey);
    return () => {
      const i = escapeStack.indexOf(entry);
      if (i >= 0) escapeStack.splice(i, 1);
      if (!escapeStack.length) window.removeEventListener('keydown', onEscapeKey);
    };
  }, [active]);
}

const WIDTH = { md: 'max-w-md', '2xl': 'max-w-2xl', '4xl': 'max-w-4xl' } as const;

export const EditorModal: React.FC<{
  title: React.ReactNode;
  onRequestClose: () => void;
  width?: keyof typeof WIDTH;
  children: React.ReactNode;
}> = ({ title, onRequestClose, width = '2xl', children }) => {
  useEscape(onRequestClose);
  // Only a tap that starts and ends outside the window counts (not a text selection dragged out).
  const downOutside = useRef(false);
  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10050] p-4"
      onPointerDown={(e) => { downOutside.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (downOutside.current && e.target === e.currentTarget) onRequestClose(); downOutside.current = false; }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={`bg-kid-card dark:bg-kid-card-dark rounded-kid-tile ${WIDTH[width]} w-full min-w-0 max-h-[90vh] overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y border-2 border-kid-border dark:border-kid-border-dark`}
      >
        <div className="sticky top-0 z-10 bg-kid-page dark:bg-kid-bg-dark border-b-2 border-kid-border dark:border-kid-border-dark pl-5 pr-3 py-3 flex justify-between items-center gap-3">
          <h3 className="min-w-0 break-words text-2xl font-bold text-kid-text dark:text-kid-text-dark">{title}</h3>
          <button
            type="button"
            onClick={onRequestClose}
            aria-label={i18n.t('editor.close')}
            title={i18n.t('editor.close')}
            className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-full text-2xl font-bold text-kid-text dark:text-kid-text-dark hover:bg-kid-card dark:hover:bg-kid-card-dark"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

/**
 * Closing an editor: right away when nothing changed; otherwise "Save your changes?" with Save /
 * Don't save / Keep editing. `snapshot` is everything the form holds; it is compared with how it
 * looked just after the editor opened. `save` runs the editor's own Save (which checks the form and
 * closes the editor itself when it saves); `discard` is what Cancel does.
 */
export function useCloseGuard({ open, snapshot, save, discard, saveDisabled = false }: {
  open: boolean;
  snapshot: unknown;
  save: () => void | Promise<void>;
  discard: () => void | Promise<void>;
  saveDisabled?: boolean;
}) {
  const latest = useRef(snapshot);
  latest.current = snapshot;
  const baseline = useRef<string | null>(null);
  const [asking, setAsking] = useState(false);

  // Taken just after opening, once the form has filled itself in.
  useEffect(() => {
    baseline.current = null;
    setAsking(false);
    if (!open) return;
    const t = setTimeout(() => { baseline.current = editorStateKey(latest.current); }, 0);
    return () => clearTimeout(t);
  }, [open]);

  const requestClose = useCallback(() => {
    const changed = baseline.current !== null && editorStateKey(latest.current) !== baseline.current;
    if (changed) setAsking(true);
    else void discard();
  }, [discard]);

  const dialog = asking ? (
    <UnsavedChangesDialog
      saveDisabled={saveDisabled}
      onSave={() => { setAsking(false); void save(); }}
      onDiscard={() => { setAsking(false); void discard(); }}
      onKeepEditing={() => setAsking(false)}
    />
  ) : null;

  return { requestClose, dialog };
}

const btn = 'inline-flex items-center justify-center min-h-[48px] px-5 rounded-full font-bold text-base w-full disabled:opacity-50';

export const UnsavedChangesDialog: React.FC<{
  onSave: () => void;
  onDiscard: () => void;
  onKeepEditing: () => void;
  saveDisabled?: boolean;
}> = ({ onSave, onDiscard, onKeepEditing, saveDisabled }) => {
  useEscape(onKeepEditing);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10062] p-4" onClick={(e) => { if (e.target === e.currentTarget) onKeepEditing(); }}>
      <div role="alertdialog" aria-modal="true" aria-labelledby="unsaved-title" className="bg-kid-card dark:bg-kid-card-dark rounded-kid-tile max-w-sm w-full p-6 border-2 border-kid-border dark:border-kid-border-dark text-kid-text dark:text-kid-text-dark">
        <h3 id="unsaved-title" className="font-kid-heading text-xl font-extrabold mb-2">{i18n.t('editor.unsaved_title')}</h3>
        <p className="mb-5">{i18n.t('editor.unsaved_body')}</p>
        <div className="flex flex-col gap-2">
          <button type="button" autoFocus onClick={onSave} disabled={saveDisabled} className={`${btn} bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_3px_0_0_#1F5C39]`}>
            {i18n.t('editor.save')}
          </button>
          <button type="button" onClick={onDiscard} className={`${btn} border-2 border-red-300 dark:border-red-800 text-red-800 dark:text-red-200`}>
            {i18n.t('editor.discard')}
          </button>
          <button type="button" onClick={onKeepEditing} className={`${btn} border-2 border-kid-border-strong dark:border-kid-border-strong-dark`}>
            {i18n.t('editor.keep_editing')}
          </button>
        </div>
      </div>
    </div>
  );
};
